import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core'
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms'
import {
  OtpComponentResources,
  DefaultOtpComponentResource,
} from './otp.component.resources'
import { AuthService } from '../../../services/auth.service';
import { Store } from '@ngrx/store';
import { GenerateOTPAction, VerifyOTPAction, VerifyRecaptchaTokenAction, VerifyRecaptchaTokenSuccessAction } from '../../../store/auth/auth.actions';
import { IClientInfo, IGenerateOTP, IGenerateOTPRequestBody } from '../../../models/authentication';
import { interval, Observable, Subject, Subscription, take, takeUntil } from 'rxjs';
import { OTPLoginStatus, selectClientInfo, selectOTPLoginStatus } from '../../../store/auth/auth.reducer';
import { config } from '../../../../config/appConfig';
import { Actions, ofType } from '@ngrx/effects';

declare const grecaptcha: any; // Declare grecaptcha

@Component({
  selector: 'app-otp',
  standalone: false,
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.scss'],
})
export class OtpComponent implements OnInit, AfterViewInit {

  @ViewChild('otpInput') otpInputRef!: ElementRef;
  @ViewChild('otpSubmitButton') otpSubmitButtonRef!: ElementRef;
  @ViewChild('contactInfoInput') contactInfoInputRef!: ElementRef;

  @Input() resources: Partial<OtpComponentResources> = {}
  @Input() allowEmail: boolean = false;
  @Input() allowMobile: boolean = false;
  finalResources!: OtpComponentResources
  form!: FormGroup<any>
  isOtpVisible: boolean = false;

  private _subscriptions: Subscription[] = [];
  private recaptchaScript: HTMLScriptElement | null = null;

  // OTP Related
  otpTimer: number = 120;
  otpTimerSubscription?: Subscription;
  otpInfo: IGenerateOTP | null = null;
  otpRegex = /^[0-9]{6}$/;
  private destroy$ = new Subject<void>();

  // Observables
  otpLoginStatus$: Observable<OTPLoginStatus>;
  clientInfo$: Observable<IClientInfo | null>;
  clientInfo: IClientInfo | null;

  constructor(private fb: FormBuilder, private authService: AuthService, private store: Store, private actions$: Actions,
    private cdr: ChangeDetectorRef) {
    this.otpLoginStatus$ = this.store.select(selectOTPLoginStatus);
    this.clientInfo$ = this.store.select(selectClientInfo);
    this.clientInfo = null
  }

  // Start the Timer
  startOtpTimer(duration: number): void {
    this.resetOtpTimer(); // Reset any running timer
    // added extra second 100 milliseconds for processing time
    const endTime = Date.now() + duration * 1000 + 100;

    this.otpTimerSubscription = interval(1000).subscribe(() => {
      const remaining = Math.max(
        0,
        Math.ceil((endTime - Date.now()) / 1000)
      );

      this.otpTimer = remaining;

      if (remaining === 0) {
        this.resetOtpTimer();
        this.onOTPTimeCompleted();
      }
    });
  }

  // Reset the Running Timer
  resetOtpTimer(): void {
    this.otpTimerSubscription?.unsubscribe();
    this.otpTimer = 120;
  }

  // Perform Action When OTP Time Is Completed
  onOTPTimeCompleted() {
    this.isOtpVisible = false;
    const contactInfoControl = this.form?.get('contactInfo');
    const otpControl = this.form?.get('otp');

    if (otpControl) {
      otpControl.reset(); // Clear OTP field
    }

    if (contactInfoControl) {
      this.cdr.detectChanges();
      contactInfoControl.enable(); // Re-enable contact info input
      setTimeout(() => {
        this.contactInfoInputRef?.nativeElement.focus();

      }, 500);
    }
  }

  // Load reCAPTCHA v3 script
  private loadRecaptchaScript(): void {
    if (document.querySelector(`script[src^="https://www.google.com/recaptcha/api.js"]`)) {
      return;
    }
    this.recaptchaScript = document.createElement('script');
    this.recaptchaScript.src = `https://www.google.com/recaptcha/api.js?render=${config.reCAPTCHA_site_key}`;
    this.recaptchaScript.async = true;
    this.recaptchaScript.defer = true;
    this.recaptchaScript.onerror = () => {
      console.error('Failed to load reCAPTCHA script');
    };
    document.head.appendChild(this.recaptchaScript);
  }

  onFormSubmit(event: Event) {
    event.preventDefault(); // Prevent default form submission
    if (this.form.get('contactInfo')?.invalid) {
      return;
    }

    if (this.clientInfo?.LoginMethodsAllowed?.GoogleRecaptcha) {
      // Dispatch the Verify Recaptcha Action
      grecaptcha.ready(() => {
        grecaptcha.execute(config.reCAPTCHA_site_key, { action: 'LOGIN' }).then((token: string) => {
          this.store.dispatch(VerifyRecaptchaTokenAction({
            request: {
              RecaptchaUserToken: token
            }
          }));
        });
      });
    } else {
      // Submit the Form to Continue the OTP Login Flow
      this.onSubmitContactInfo();
    }
  }

  // To Handle Focus on Contact Info Input
  ngAfterViewInit(): void {
    this.cdr.detectChanges();
    setTimeout(() => {
      this.contactInfoInputRef?.nativeElement.focus();
    }, 500); // Small delay to handle dynamic rendering
  }

  onPaste(event: ClipboardEvent) {
    const pastedData = event.clipboardData?.getData('text') ?? '';

    if (!this.otpRegex.test(pastedData)) {
      event.preventDefault();
      return;
    }
  }

  onOtpInputKeyDown(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'];
    const isPasting = event.code === 'KeyV' && event.ctrlKey;

    if (allowedKeys.indexOf(event.key) === -1 && isNaN(Number(event.key)) && !isPasting) {
      event.preventDefault();
    }

    // Corrected check using nullish coalescing or optional chaining
    if ((this.form.get('otp')?.value?.length || 0) >= 6 &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      !isPasting &&
      allowedKeys.indexOf(event.key) === -1) {
      event.preventDefault();
    }

    if (event.key === 'Enter' && this.isOtpValid()) {
      this.onSubmitOtp();
    }
  }

  onOTPInput(event: Event) {
    const target = (event?.target as HTMLInputElement);
    let value = target?.value.trim();
    // if otp length is greater than 6 digits, then only keep first 6 digits
    if (value.length > 6) {
      value = value.slice(0,6);
      target.value = value;
    }
    // to remove any white spaces, if directly copy pasted OTP
    this.form.patchValue({
      otp: value.trim()
    })
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      contactInfo: ['', [Validators.required, this.contactInfoValidator.bind(this)]],
      otp: [
        '',
        [Validators.required, Validators.pattern(this.otpRegex)]
      ],
    })

    this.finalResources = { ...DefaultOtpComponentResource, ...this.resources };

    // Handle client info for loading the recaptcha script
    this._subscriptions.push(
      this.clientInfo$.subscribe(clientInfo => {
        if (!clientInfo) return;
        this.clientInfo = clientInfo;
        if (this.clientInfo?.LoginMethodsAllowed?.GoogleRecaptcha) {
          // Load the Recaptcha Script
          this.loadRecaptchaScript();
        } else {
          // Remove the Recaptcha Script
          this.recaptchaScript?.remove();
          this.recaptchaScript = null;
          document.querySelector('.grecaptcha-badge')?.remove();
        }
      })
    );

    // Ensure Validation Errors appear immediately for all input fields when the user starts typing
    Object.keys(this.form.controls).forEach(field => {
      this.form.get(field)?.valueChanges.subscribe(() => {
        this.form.get(field)?.markAsTouched();
      });
    });


    // SubScribe To OTP Login State
    this._subscriptions.push(
      this.otpLoginStatus$.subscribe((status) => {
        const contactInfoControl = this.form?.get('contactInfo');
        const otpControl = this.form?.get('otp');
        if (!contactInfoControl?.valid) {
          return
        }
        switch (status) {
          case OTPLoginStatus.GenerateOTPSuccess:
            this.isOtpVisible = true;
            contactInfoControl.disable();

            // Get the OTP Info From the Auth Service
            this.otpInfo = this.authService.getOTPGenerateSuccessInfo();
            if (this.otpInfo?.ExpiresIn) {
              this.otpTimer = this.otpInfo.ExpiresIn;
              this.startOtpTimer(this.otpTimer - 1);
            }

            // Make the OTP Input In Focus
            otpControl?.enable();

            // Focus the OTP input when it becomes visible
            setTimeout(() => this.otpInputRef?.nativeElement.focus(), 200);

            break;
          case OTPLoginStatus.GenerateOTPFailure:
            break;
        }
      })
    )

    // Verify Captcha Success
    this._subscriptions.push(
      this.actions$.pipe(
        ofType(VerifyRecaptchaTokenSuccessAction),
        takeUntil(this.destroy$)
      ).subscribe(({ response }) => {
        // Submit the Form to Continue the OTP Login Flow
        this.onSubmitContactInfo();
      })
    );
  }

  ngOnDestroy() {
    this._subscriptions.forEach((sub) => sub.unsubscribe());
    this.resetOtpTimer();

    this.destroy$.next();
    this.destroy$.complete();
    // Remove reCAPTCHA script
    if (this.recaptchaScript) {
      this.recaptchaScript.remove();
      this.recaptchaScript = null;
    }
    // Remove reCAPTCHA badge
    const badge = document.querySelector('.grecaptcha-badge');
    if (badge) {
      badge.remove();
    }
  }

  // Validate Email and Phone Number Patters
  contactInfoValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return { required: true }; // Explicitly return required error if empty
    }

    const mobilePattern = /^[6-9][0-9]{9}$/;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    let isValidEmail = false;
    let isValidMobile = false;


    if (this.allowEmail) {
      isValidEmail = emailPattern.test(value); // Use regex directly for email
    }

    if (this.allowMobile) {
      isValidMobile = mobilePattern.test(value);
    }

    // If neither email nor mobile is valid and at least one is allowed
    if (!isValidEmail && !isValidMobile && (this.allowEmail || this.allowMobile)) {
      return { invalidContact: true };
    }

    // Further validation for mobile if allowed and valid
    if (isValidMobile && this.allowMobile) {
      const allSameDigits = /^(\d)\1{9}$/.test(value);
      const sequentialAscending = '0123456789' === value;
      const sequentialDescending = '9876543210' === value;
      const repeatedPattern = /^(\d{2})\1{4}$/.test(value) || /^(\d{5})\1{1}$/.test(value);
      const tooManyRepeats = /(.)\1{5,}/.test(value);

      if (allSameDigits || sequentialAscending || sequentialDescending || repeatedPattern || tooManyRepeats) {
        return { invalidContact: true };
      }
    }

    // Ensure input matches the allowed type
    if (this.allowEmail && !this.allowMobile && !isValidEmail) {
      return { invalidContact: true };
    }
    if (this.allowMobile && !this.allowEmail && !isValidMobile) {
      return { invalidContact: true };
    }

    return null; // Input is valid
  }

  // Handle the "Get OTP" button click
  onSubmitContactInfo() {
    const contactInfoControl = this.form?.get('contactInfo')
    if (!contactInfoControl?.valid) {
      return
    }
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    const isEmail = emailPattern.test(contactInfoControl.value);

    let deliveryMode: "EMAIL" | "SMS" = "EMAIL";
    if (this.allowEmail && isEmail) {
      deliveryMode = "EMAIL";
    } else if (this.allowMobile && !isEmail) {
      deliveryMode = "SMS";
    } else {
      // Handle case where input doesn't match allowed types
      this.form.get('contactInfo')?.setErrors({ invalidContact: true });
      return;
    }

    const otpInfo: IGenerateOTPRequestBody = {
      Receiver: contactInfoControl.value,
      DeliveryMode: deliveryMode,
    }

    this.store.dispatch(GenerateOTPAction({
      request: otpInfo
    }))

    // Focus the OTP input after dispatching GenerateOTPAction
    setTimeout(() => {
      if (this.isOtpVisible) {
        this.otpInputRef?.nativeElement.focus();
      }
    }, 200); // Small delay to ensure the input is rendered
  }

  // Edit function to reset the form and restore previous contact info
  onEdit() {
    // Store the original value before resetting
    const originalContactInfo = this.form.get('contactInfo')?.value

    this.form?.reset()

    // Restore the original value
    this.form.get('contactInfo')?.setValue(originalContactInfo)

    const contactInfoControl = this.form?.get('contactInfo')
    contactInfoControl?.enable() // Re-enable the contact info field
    this.isOtpVisible = false // Hide OTP input
  }

  // OTP Validation Check
  isOtpValid(): boolean {
    return this.form?.get('otp')?.valid ?? false
  }

  // Utility function to check form errors
  hasError(controlName: string, errorType: string): boolean {
    const control = this.form?.get(controlName)
    return (control?.hasError(errorType) && control?.dirty) || false
  }

  // Handle OTP submission
  onSubmitOtp() {
    const otpControl = this.form?.get('otp');
    const otpGenerateInfo = this.authService.getOTPGenerateSuccessInfo();
    if (otpControl?.valid && otpGenerateInfo) {
      this.store.dispatch(VerifyOTPAction({
        request: {
          VerificationToken: otpGenerateInfo.VerificationToken,
          UserInput: otpControl.value,
          Receiver: this.form?.get('contactInfo')?.value,
        }
      }))
    }
  }
}
